import * as React from "react";
import { css } from "@emotion/react";
import { EditorProfile } from "main/javascripts/components/molecules/Post/EditorProfile";
import { TagList } from "main/javascripts/features/post/components/TagList";
import { RegionTagList } from "main/javascripts/components/molecules/Post/RegionTagList";
import { TableOfContents } from "main/javascripts/components/molecules/Post/TableOfContents/TableOfContents";
import { PopularTagList } from "main/javascripts/components/molecules/Post/PopularTagList";
import { RankingList } from "main/javascripts/features/post/components/RankingList";
import { RelatedPostList } from "main/javascripts/features/post/components/RelatedPostList";
import { FixedLineButton } from "main/javascripts/components/molecules/Post/FixedLineButton";
import { Search } from "main/javascripts/features/post/components/Search";
import { useAppSelector } from "main/javascripts/store";
import { postSelector } from "../postSlice";
import {
  backgroundColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { PostContent } from "./PostContent";

export interface IProps {
  html?: string;
}

export const PostShowMainTabiChat: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { html } = props;
  const post = useAppSelector(postSelector);

  const renderTags = (): JSX.Element | null => {
    const hasTags: boolean = post.tags.length > 0 || post.regions.length > 0;
    if (!hasTags) {
      return null;
    }

    return (
      <div css={tagBlockStyle}>
        {post.tags.length > 0 && (
          <div css={tagListBlockStyle}>
            <TagList tags={post.tags} />
          </div>
        )}
        {post.regions.length > 0 && (
          <RegionTagList regions={post.regions} disableLink />
        )}
      </div>
    );
  };

  const hasSearchTypes = post.searchTypes?.length > 0;

  return (
    <div css={blockStyle}>
      <main css={innerStyle}>
        <div css={mainStyle}>
          <article css={postBlockStyle}>
            <header>
              <div css={headerInnerStyle}>
                <h1 css={postTitleStyle}>{post.title}</h1>
                {post.summary && (
                  <div css={postSummaryStyle}>{post.summary}</div>
                )}
                <Search />
                {post.featuredImage && (
                  <div css={featuredImageBlockStyle}>
                    <img
                      css={featuredImageStyle}
                      src={post.featuredImage.url}
                      alt={post.title}
                    />
                  </div>
                )}
              </div>
            </header>
            <TableOfContents editorStateJson={post.content.editorStateJson} />
            <section css={contentBlockStyle}>
              <PostContent html={html} />
            </section>
            <section css={contentBlockStyle}>
              {renderTags()}
              {post.editor && (
                <div css={editorProfileBlockStyle}>
                  <EditorProfile
                    editor={post.editor}
                    updatedAt={post.updatedAt}
                  />
                </div>
              )}
            </section>
          </article>
          <section css={postBottomBlockStyle}>
            <RelatedPostList posts={post.relatedPosts} />
          </section>
        </div>
        <div css={sideStyle}>
          <div css={popularTagsBlockStyle}>
            <PopularTagList tags={post.popularTags} />
          </div>
          <div css={rankingBlockStyle}>
            <RankingList rankings={post.rankings} />
          </div>
        </div>
        <FixedLineButton
          lineFooterText={post.lineFooterText}
          hasSearchTypes={hasSearchTypes}
          post={post}
        />
      </main>
    </div>
  );
};

const blockStyle = css`
  background-color: ${backgroundColor.secondaryLightColor};
  ${moreThanBreakpoint("desktop")} {
    min-height: calc(100vh - 357px);
  }
`;
const innerStyle = css`
  max-width: 50rem;
  margin: 0 auto;
  padding: ${space.atom} 0;
  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom3x} ${space.atom3x};
  }
  ${moreThanBreakpoint("desktop")} {
    max-width: 72rem;
    display: flex;
    justify-content: space-between;
    padding: ${space.atom3x} ${space.atom3x} 3rem;
  }
`;
const mainStyle = css`
  width: 100%;
  ${moreThanBreakpoint("desktop")} {
    width: calc(100% - 21.5rem);
  }
`;
const sideStyle = css`
  padding-top: ${space.atom4x};
  ${moreThanBreakpoint("desktop")} {
    padding-top: 0;
    width: 20rem;
  }
`;
const postBlockStyle = css`
  background-color: #fff;
`;
const headerInnerStyle = css`
  padding: ${space.atom} ${space.atom} 0;
  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom3x} ${space.atom3x} 0;
  }
`;
const featuredImageBlockStyle = css`
  position: relative;
  padding: ${space.atom3x} ${space.atom} ${space.atom2x};
`;
const featuredImageStyle = css`
  display: block;
  width: 100%;
`;
const contentBlockStyle = css`
  padding: ${space.atom2x} ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom2x} ${space.atom3x};
  }
`;
const postTitleStyle = css`
  font-size: ${fontSize.headline};
  line-height: ${lineHeight.headline1};
  font-weight: ${fontWeight.bold};
  color: ${textColor.primaryDarkColor};
  padding: ${space.atom} ${space.atom};
  margin: 0;
  ${moreThanBreakpoint("tablet")} {
    font-size: 1.75rem;
    line-height: 1.5;
  }
`;
const postSummaryStyle = css`
  margin: ${space.atom} ${space.atom} ${space.atom2x};
  padding: ${space.atom2x} ${space.atom2x};
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body1};
  color: ${textColor.secondaryDarkColor};
  background-color: ${backgroundColor.subsectionColor};
  ${moreThanBreakpoint("tablet")} {
    margin-top: ${space.atom2x};
  }
`;
const editorProfileBlockStyle = css`
  padding: ${space.atom} ${space.atom} ${space.atom} 0;
`;
const tagBlockStyle = css`
  padding: ${space.atom} 0 0;
`;
const tagListBlockStyle = css`
  padding: 0 ${space.atom};
`;
const popularTagsBlockStyle = css`
  padding: 0 0 ${space.atom2x};
`;
const rankingBlockStyle = css``;
const postBottomBlockStyle = css`
  margin-top: ${space.atom4x};
`;
